import clsx from "clsx";
import * as React from "react";

export type SelectOption = {
    value: string | number;
    label: string;
};

export type SelectOptions = SelectOption[];

export type SelectProps = {
    options: SelectOptions;
    value?: string | number | readonly string[];
    name?: string;
    label?: string;
    labelContent?: () => React.ReactNode | void;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    action?: () => void;
    input?: Partial<
        React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>
    >;
    inputComponent?: React.ReactNode;
    wrapper?: React.DetailedHTMLProps<
        React.LabelHTMLAttributes<HTMLLabelElement>,
        HTMLLabelElement
    >;
    classes?: {
        wrapper?: string;
        input?: string;
        input_container?: string;
    };
    loading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    disableDefaultOption?: boolean;

    /**
     * Mostly used with an object with the message key as a react node or string
     */
    error?: { [key: string]: any };
    errorMessage?: React.ReactNode | string;
    successMessage?: React.ReactNode | string;
    iconBefore?: React.ReactNode | string;
    iconAfter?: React.ReactNode | string;
    iconBeforeContainer?: React.HTMLAttributes<HTMLDivElement>;
    iconAfterContainer?: React.HTMLAttributes<HTMLDivElement>;
};

export default function Select({
    classes = {},
    disabled = false,
    input = {},
    iconBeforeContainer = {},
    iconAfterContainer = {},
    placeholder,
    loading = false,
    options,
    disableDefaultOption = false,
    value,
    ...props
}: SelectProps) {
    const onChange = (e) => {
        if (props?.onChange) {
            props?.onChange(e);
        }
    };

    const input_styles: any = {};

    if (props.iconBefore) {
        input_styles.paddingLeft = 0;
    }

    if (props.iconAfter) {
        input_styles.paddingRight = 0;
    }

    if (input_styles) {
        if (input.style) {
            input.style = { ...input_styles, ...input.style };
        } else {
            input.style = input_styles;
        }
    }

    iconBeforeContainer.className = clsx("tmp-input-icon-container", iconBeforeContainer.className);

    iconAfterContainer.className = clsx("tmp-input-icon-container", iconAfterContainer.className);

    return (
        <label className={clsx(classes?.wrapper, ["tmp-label"])}>
            {props?.label ? <div className="tmp-label-text">{props?.label}</div> : null}
            <div
                className={clsx("tmp-input-container", {
                    "tmp-disabled": disabled,
                    "tmp-input-error": props.errorMessage,
                })}
            >
                {props.iconBefore && typeof props.iconBefore === "string" && (
                    <div {...iconBeforeContainer}>
                        <i className={props.iconBefore} />
                    </div>
                )}
                {props.iconBefore && typeof props.iconBefore !== "string" && props.iconBefore}
                <select
                    className={clsx(classes.input, ["tmp-input", "tmp-select"])}
                    value={value ?? (disableDefaultOption ? undefined : "default")}
                    onChange={onChange}
                    {...input}
                    disabled={disabled}
                >
                    {!disableDefaultOption && (
                        <option disabled={true} value="default">
                            {placeholder ?? "Vælg"}
                        </option>
                    )}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {loading ? (
                    <div {...iconAfterContainer}>
                        <i className="fas fa-spinner fa-pulse" />
                    </div>
                ) : (
                    <>
                        {props.iconAfter && typeof props.iconAfter === "string" && (
                            <div {...iconAfterContainer}>
                                <i className={props.iconAfter} />
                            </div>
                        )}
                        {props.iconAfter && typeof props.iconAfter !== "string" && props.iconAfter}
                    </>
                )}
            </div>
            {props.errorMessage && (
                <div className="tmp-input-error-message">{props.errorMessage}</div>
            )}
            {props.successMessage && (
                <div className="tmp-input-success-message">{props.successMessage}</div>
            )}
        </label>
    );
}
